import {
  useInfiniteQuery,
  keepPreviousData,
  useQuery,
  useMutation,
} from "@tanstack/react-query";
import { FilterProps } from "../../types/AppTypes";
import { useNotification } from "../notification.hook";
import { PotentialMatrixFilters } from "../usePotentialMatrixFilters.hook";
import PotentialMatrixPerformanceService from "../../services/api/potentialMatrix/PotentialMatrixPerformance.service";
import {
  MATRIX_COLLABORATORS_PAGE_SIZE,
  RefreshReactQuery,
} from "../../constants/AppConstants";
import { ERROR_LABEL, ERROR_MESSAGE_LABEL } from "../../constants/Dictionary";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import { PotentialMatrixFilterKeyEnum } from "../../types/api/filters.type";
import { MatrixPerformanceItem } from "../../types/api/potentialMatrix.type";
import PotentialMatrixPerformanceCollaboratorsService from "../../services/api/potentialMatrix/PotentialMatrixPerformanceCollaborators.service";
import { TypeMatrixEnum } from "../../constants/PotentialMatrixConstants";

interface usePotentialMatrixPerformanceProps {
  filters: PotentialMatrixFilters;
  createQueryParams: () => FilterProps[];
  enabled: boolean;
}

export const usePotentialMatrixPerformance = ({
  filters,
  createQueryParams,
  enabled,
}: usePotentialMatrixPerformanceProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: ["get-potential-matrix-perfomance", filters],
    queryFn: ({ signal }) =>
      PotentialMatrixPerformanceService.get(createQueryParams(), signal),
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return {
    data,
    isLoading,
    isFetching,
  };
};

interface useMatrixPerformanceCollaboratorsProps {
  filters: PotentialMatrixFilters;
  enabled: boolean;
}

export const useMatrixPerformanceCollaborators = ({
  filters,
  enabled,
}: useMatrixPerformanceCollaboratorsProps) => {
  const { notification } = useNotification();
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebounce(search, 500);

  const { data, isLoading, isFetching, hasNextPage, fetchNextPage, isError } =
    useInfiniteQuery({
      queryKey: [
        "get-matrix-performance-collaborators",
        filters,
        debouncedSearch,
      ],
      queryFn: ({ pageParam, signal }) =>
        PotentialMatrixPerformanceCollaboratorsService.list(
          MATRIX_COLLABORATORS_PAGE_SIZE,
          pageParam as number,
          [
            { key: PotentialMatrixFilterKeyEnum.YEAR, value: [filters.year] },
            {
              key: PotentialMatrixFilterKeyEnum.TYPE,
              value: [filters.type.value],
            },
            {
              key: PotentialMatrixFilterKeyEnum.PRACTICE,
              value: filters.practice,
            },
            {
              key: PotentialMatrixFilterKeyEnum.COMPETENCE,
              value: filters.cc,
            },
            ...(filters.internalGrade.length > 0 &&
            filters.internalGrade[0] !== "all"
              ? [
                  {
                    key: PotentialMatrixFilterKeyEnum.INTERNAL_GRADE,
                    value: filters.internalGrade,
                  },
                ]
              : []),
            ...(debouncedSearch
              ? [{ key: "search", value: [debouncedSearch] }]
              : []),
          ],
          signal,
        ),
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        allPages.length * MATRIX_COLLABORATORS_PAGE_SIZE <
        lastPage.pagination.total
          ? allPages.length
          : undefined,
      staleTime: RefreshReactQuery.MODERATE,
      enabled,
    });

  useEffect(() => {
    if (isError) notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
  }, [isError]);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );

  const fetchMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const clearSearch = useCallback(() => setSearch(""), []);

  return {
    data: items,
    initLoading: isLoading,
    canLoadMore: hasNextPage ?? false,
    fetchMore,
    fetchingMore: isFetching && (data?.pages.length ?? 0) > 0,
    search,
    setSearch,
    clearSearch,
  };
};

interface usePerformanceMatrixDetailsProps {
  enabled: boolean;
  searchDebouncedValue: string;
  item: MatrixPerformanceItem;
  pageNumber: number;
  filters: PotentialMatrixFilters;
}

export const usePerformanceMatrixDetails = ({
  enabled,
  searchDebouncedValue,
  item,
  pageNumber,
  filters,
}: usePerformanceMatrixDetailsProps) => {
  const { notification } = useNotification();
  const { data, isLoading, isFetching, isError } = useQuery({
    queryKey: [
      "get-matrix-performance-collaborators",
      searchDebouncedValue,
      item,
      pageNumber,
      filters,
    ],
    queryFn: ({ signal }) =>
      PotentialMatrixPerformanceCollaboratorsService.list(
        MATRIX_COLLABORATORS_PAGE_SIZE,
        pageNumber,
        [
          { key: PotentialMatrixFilterKeyEnum.YEAR, value: [filters.year] },
          {
            key: PotentialMatrixFilterKeyEnum.TYPE,
            value: [filters.type.value],
          },
          ...(filters.practice.length > 0
            ? [
                {
                  key: PotentialMatrixFilterKeyEnum.PRACTICE,
                  value: filters.practice,
                },
              ]
            : []),
          ...(filters.cc.length > 0
            ? [
                {
                  key: PotentialMatrixFilterKeyEnum.COMPETENCE,
                  value: filters.cc,
                },
              ]
            : []),
          ...(filters.internalGrade.length > 0 &&
          filters.internalGrade[0] !== "all"
            ? [
                {
                  key: PotentialMatrixFilterKeyEnum.INTERNAL_GRADE,
                  value: filters.internalGrade,
                },
              ]
            : []),
          ...(filters.hiddenCollaborators.length > 0
            ? [
                {
                  key: PotentialMatrixFilterKeyEnum.HIDDEN_COLLABORATORS,
                  value: filters.hiddenCollaborators,
                },
              ]
            : []),
          ...[
            { key: "dimension", value: [item!.dimensionId] },
            { key: "impact", value: [item!.impact] },
          ],
          ...(searchDebouncedValue
            ? [{ key: "search", value: [searchDebouncedValue] }]
            : []),
        ],
        signal,
      ),
    staleTime: RefreshReactQuery.MODERATE,
    enabled,
  });

  useEffect(() => {
    if (isError) {
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    }
  }, [isError]);

  return { data, isLoading, isFetching };
};

interface useMatrixPerformanceDownloadProps {
  filters: PotentialMatrixFilters;
}

export const useMatrixPerformanceDownload = ({
  filters,
}: useMatrixPerformanceDownloadProps) => {
  const { notification } = useNotification();

  const { mutateAsync } = useMutation({
    mutationFn: (downloadLimit: number) =>
      PotentialMatrixPerformanceService.get([
        { key: "year", value: [filters.year] },
        { key: "type", value: [TypeMatrixEnum.FINAL_EVALUATION] },
        { key: "collaboratorsLimit", value: [downloadLimit] },
        { key: "collaboratorsSortBy", value: ["internalGrade"] },
        { key: "collaboratorsSortType", value: ["asc"] },
        { key: "practice", value: [filters.practice[0]] },
        { key: "competence", value: [filters.cc[0]] },
        ...(filters.internalGrade.length > 0 &&
        filters.internalGrade[0] !== "all"
          ? [{ key: "internalGrade", value: filters.internalGrade }]
          : []),
      ]),
    onError: (error) => {
      console.error(`${error}`);
      notification.error(ERROR_LABEL, ERROR_MESSAGE_LABEL);
    },
  });

  return { mutateAsync };
};
